/**
 * Der Lebenszyklus einer Ordnungsstruktur am Bildschirm -- fuer
 * Hauptkategorie und Produktfamilie dieselbe Komponente
 * (Kategorie- & Familien-Lifecycle 001).
 *
 * WARUM EINE KOMPONENTE FUER BEIDE
 *
 * Aus demselben Grund wie im Server: die Handlungen sind dieselben, nur
 * der Gegenstand ist ein anderer. Zwei Bedienoberflaechen fuer dasselbe
 * Verhalten waeren zwei Orte, an denen ein Sperrgrund vergessen werden
 * kann.
 *
 * DIE DREI FAELLE, DIE HIER UNTERSCHIEDEN WERDEN
 *
 *   A  Eine echte Produktserie faellt aus dem Sortiment  -> ARCHIVIEREN.
 *      Sie bleibt gueltig und lesbar, wird nur nicht mehr angeboten.
 *   B  Eine versehentlich angelegte, leere Struktur soll weg
 *      -> PAPIERKORB, Preflight, endgueltig loeschen.
 *   C  Eine historisch benutzte Struktur haengt an Modellen, Artikeln,
 *      Texten oder Vorgaengen -> sie wird NICHT geloescht. Der Grund
 *      steht im Klartext daneben, nicht nur als grauer Knopf.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { StructureDeletePreview, StructureKind } from "@homebase/shared";
import { api } from "../lib/api.ts";
import { useI18n } from "../i18n/index.tsx";
import { errorText } from "../i18n/errors.ts";
import { LoadingState } from "./States.tsx";

export function StructureLifecycle({ kind, id, code, name, mayManage, mayDelete, onChanged, onDeleted }: {
  kind: StructureKind;
  id: number;
  code: string;
  name: string;
  /** `article.write` fuer Familien, `system.manage` fuer Hauptkategorien. */
  mayManage: boolean;
  /** Zusaetzlich `article.delete` -- dieselbe Schwelle wie am Artikel. */
  mayDelete: boolean;
  onChanged: () => void;
  onDeleted: () => void;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [v, setV] = useState<StructureDeletePreview | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [bearbeiten, setBearbeiten] = useState(false);
  const [neuerName, setNeuerName] = useState(name);
  const [neueKennung, setNeueKennung] = useState(code);
  const [loeschen, setLoeschen] = useState(false);
  const [bestaetigung, setBestaetigung] = useState("");
  const laeuft = useRef(false);

  const laden = useCallback(async () => {
    try { setV(await api.structurePreview(kind, id)); } catch (e) { setError(e); }
  }, [kind, id]);
  useEffect(() => { void laden(); }, [laden]);
  useEffect(() => { setNeuerName(name); setNeueKennung(code); }, [name, code]);

  async function tun(fn: () => Promise<StructureDeletePreview>): Promise<void> {
    if (laeuft.current) return;
    laeuft.current = true; setBusy(true); setError(null);
    try { setV(await fn()); onChanged(); }
    catch (e) { setError(e); }
    finally { setBusy(false); laeuft.current = false; }
  }

  async function endgueltig(): Promise<void> {
    if (laeuft.current) return;
    laeuft.current = true; setBusy(true); setError(null);
    try { await api.purgeStructure(kind, id, bestaetigung.trim()); onDeleted(); }
    catch (e) { setError(e); }
    finally { setBusy(false); laeuft.current = false; }
  }

  const art = kind === "category" ? t("sl.category") : t("sl.family");
  /* Die Kennung ist nur aenderbar, solange nichts an der Struktur haengt --
     der Grund steht daneben, nicht in einer Fehlermeldung nach dem
     Speichern. */
  const haengt = (v?.references ?? []).filter((r) => r.count > 0);
  const kennungOffen = haengt.length === 0;

  return (
    <section className="models__block sl" data-testid="sl-lifecycle">
      <h3 className="detail__section">{t("sl.title", { art })}</h3>
      {!v && error === null && <LoadingState />}
      {v && (<>
        <p className="sl__zustand" data-testid="sl-zustand">
          {v.trashed ? <span className="sl__marke sl__marke--papierkorb">{t("sl.stateTrashed")}</span>
            : v.archived ? <span className="sl__marke sl__marke--archiv">{t("sl.stateArchived")}</span>
            : <span className="sl__marke sl__marke--aktiv">{t("sl.stateActive")}</span>}
          {" "}
          <span className="models__note">
            {haengt.length === 0 ? t("sl.noDependants")
              : t("sl.dependants", { list: haengt.map((h) => `${h.count} ${h.label}`).join(", ") })}
          </span>
        </p>

        {/* ── Bearbeiten ─────────────────────────────────────────────── */}
        {mayManage && !bearbeiten && !v.trashed && (
          <button className="btn btn--small sl__bearbeiten" data-testid="sl-bearbeiten"
            onClick={() => setBearbeiten(true)}>{t("common.edit")}</button>
        )}
        {mayManage && bearbeiten && (
          <div className="models__form models__form--wide" data-testid="sl-form">
            <div className="feldgruppe">
              <label className="feld">
                <span className="feld__label">{t("sl.name")}</span>
                <input className="feld__input" data-testid="sl-name" value={neuerName} maxLength={120}
                  onChange={(e) => setNeuerName(e.target.value)} />
              </label>
              <label className="feld feld--kurz">
                <span className="feld__label">{t("sl.code")}</span>
                <input className="feld__input" data-testid="sl-code" value={neueKennung} maxLength={60}
                  disabled={!kennungOffen} onChange={(e) => setNeueKennung(e.target.value)} />
                <small className="feld__note" data-testid="sl-code-grund">
                  {kennungOffen ? t("sl.codeFree") : t("sl.codeLocked")}
                </small>
              </label>
            </div>
            <div className="models__formactions">
              <button className="btn btn--primary sl__speichern" data-testid="sl-speichern"
                disabled={busy || neuerName.trim() === "" || neueKennung.trim() === ""}
                onClick={() => void tun(async () => {
                  const r = await api.updateStructure(kind, id, {
                    name: neuerName.trim(),
                    ...(kennungOffen && neueKennung.trim() !== code ? { code: neueKennung.trim() } : {}),
                  });
                  setBearbeiten(false);
                  return r;
                })}>{t("common.save")}</button>
              <button className="btn" onClick={() => {
                setBearbeiten(false); setNeuerName(name); setNeueKennung(code); setError(null);
              }}>{t("common.cancel")}</button>
            </div>
          </div>
        )}

        {/* ── Archivieren, Reaktivieren, Papierkorb ──────────────────── */}
        {mayManage && (
          <div className="sl__aktionen">
            {!v.trashed && !v.archived && (
              <button className="btn btn--small sl__archivieren" data-testid="sl-archivieren" disabled={busy}
                onClick={() => void tun(() => api.archiveStructure(kind, id))}>{t("sl.archive")}</button>
            )}
            {v.archived && (
              <button className="btn btn--small sl__reaktivieren" data-testid="sl-reaktivieren" disabled={busy}
                onClick={() => void tun(() => api.reactivateStructure(kind, id))}>{t("sl.reactivate")}</button>
            )}
            {!v.trashed && (
              <button className="btn btn--small sl__papierkorb" data-testid="sl-papierkorb" disabled={busy}
                onClick={() => void tun(() => api.trashStructure(kind, id))}>{t("sl.toTrash")}</button>
            )}
            {v.trashed && (
              <button className="btn btn--small sl__wiederherstellen" data-testid="sl-wiederherstellen" disabled={busy}
                onClick={() => void tun(() => api.restoreStructure(kind, id))}>{t("sl.restore")}</button>
            )}
          </div>
        )}
        <p className="models__note">{t("sl.archiveExplains")}</p>

        {/* ── Endgueltig loeschen ────────────────────────────────────── */}
        {mayDelete && v.trashed && (
          <div className="sl__gefahr">
            {!v.deletable ? (
              <p className="models__note models__note--strong" data-testid="sl-gesperrt">
                {v.reasons.join(" ")}
              </p>
            ) : !loeschen ? (
              <button className="btn btn--small btn--danger sl__loeschenknopf" data-testid="sl-loeschen-oeffnen"
                onClick={() => setLoeschen(true)}>{t("sl.purgeOpen")}</button>
            ) : (
              <div data-testid="sl-loeschen">
                <p>{t("sl.purgeText", { art, code: v.code })}</p>
                {v.losing.length > 0 && (
                  <p className="models__note models__note--strong" data-testid="sl-verlust">
                    {t("sl.purgeLosing", { list: v.losing.map((x) => `${x.label} ${x.count}`).join(", ") })}
                  </p>
                )}
                <label className="feld feld--kurz">
                  <span className="feld__label">{t("sl.purgeConfirmLabel", { code: v.code })}</span>
                  <input className="feld__input sl__nummer" data-testid="sl-bestaetigung" value={bestaetigung}
                    autoFocus onChange={(e) => setBestaetigung(e.target.value)} />
                </label>
                <div className="models__formactions">
                  <button className="btn btn--danger sl__endgueltig" data-testid="sl-endgueltig"
                    disabled={busy || bestaetigung.trim() !== v.code}
                    onClick={() => void endgueltig()}>{t("sl.purge")}</button>
                  <button className="btn" onClick={() => { setLoeschen(false); setBestaetigung(""); }}>
                    {t("common.cancel")}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </>)}
      {error !== null && <p className="models__error" role="alert">{errorText(i18n, error)}</p>}
    </section>
  );
}
